import type { Role, RoleAssignment } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import type { SectionRendererSection } from '@adornis/entity-relationship-management/client/details/SectionRenderer.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import type { Client } from '@nsc/clients/entities/Client.js';
import { userRoleAssignmentSection } from '@nsc/nexus-permissions/client/erm-shared-admin.js';
import { subscribePlatformRoleAssignmentFor } from '@nsc/nexus-permissions/nexus-api/platform-gated-reads.js';
import { NexusRoleNames } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { NEXUS_ADMIN_ROLE_NAME } from '@nsc/nexus-permissions/platform-rbac/platform-admin-permissions.js';
import { subscribeAllEntitiesRoles } from '@nsc/nexus-permissions/subscribe-all-roles.js';
import { html, nothing } from 'lit';
import { subscribeAllClientsForNexus } from '../../api/nexus-entity-list-subscriptions.js';
import {
  addClientToScope,
  getClientIdsFromScope,
  removeClientFromScope,
} from '../../features/permissions/scope-helpers.js';

export const nexusRoleSection = userRoleAssignmentSection({
  title: 'Nexus roles',
  roleNameFilter: n => {
    const name = n ?? '';
    return name.startsWith('Nexus ') || name === NEXUS_ADMIN_ROLE_NAME;
  },
  placeholder: 'Select roles …',
  emptyState: 'No roles found',
  hint: 'Nexus roles and NSC Admin (platform super-admin).',
  dialogLoading: 'Updating roles…',
});

const ClientScopeEditor = stateful(({ user }: { user: AdornisUser<any> }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const rolesQuery = useQuery(() => subscribeAllEntitiesRoles()({ _id: 1, roleName: 1 }), []);
  const assignmentQuery = useQuery<RoleAssignment | null>(
    () => subscribePlatformRoleAssignmentFor(user._id)({ _id: 1, roleIds: 1, scope: { contexts: 1, permission: 1 } }),
    [user._id, refreshKey],
  );
  const clientsQuery = useQuery(() => subscribeAllClientsForNexus()({ _id: 1, name: 1 }), []);

  const roles = (rolesQuery.value ?? []) as Role[];
  const assignment = assignmentQuery.value;
  const clients = (clientsQuery.value ?? []) as Client[];
  const editorRole = roles.find(r => r.roleName === NexusRoleNames.NEXUS_CLIENT_EDITOR);
  const viewerRole = roles.find(r => r.roleName === NexusRoleNames.NEXUS_CLIENT_VIEWER);
  const adminRoleId = roles.find(r => r.roleName === NEXUS_ADMIN_ROLE_NAME)?._id;
  const hasScopedNexusRole = assignment?.roleIds?.some(id => id === editorRole?._id || id === viewerRole?._id) ?? false;

  if (rolesQuery.loading || assignmentQuery.loading || clientsQuery.loading) {
    return html`<span class="loading loading-spinner loading-sm text-primary"></span>`;
  }

  if (!hasScopedNexusRole) {
    return html`<p class="text-sm text-base-content/50">Assign Nexus Client Editor or Viewer to scope clients.</p>`;
  }

  if (adminRoleId && assignment?.roleIds?.includes(adminRoleId)) {
    return html`<p class="text-sm text-base-content/50">Admin role has access to all clients.</p>`;
  }

  const assignedIds = new Set(getClientIdsFromScope(assignment?.scope ?? undefined));
  const selectedClients = clients.filter(c => assignedIds.has(c._id));
  const available = clients.filter(c => !assignedIds.has(c._id));
  const refresh = () => setRefreshKey(refreshKey + 1);

  const handleAdd = async (client: Client) => {
    await guardDialog(
      async () => {
        assignment!.scope = addClientToScope(assignment!.scope ?? undefined, client._id);
        await assignment!.save?.();
      },
      {
        loadingText: 'Adding client…',
        successText: 'Client added to scope.',
        errorMessagePrefix: 'Could not add client',
      },
    );
    refresh();
  };

  const handleRemove = async (clientId: string) => {
    await guardDialog(
      async () => {
        assignment!.scope = removeClientFromScope(assignment!.scope ?? undefined, clientId);
        await assignment!.save?.();
      },
      {
        loadingText: 'Removing client…',
        successText: 'Client removed from scope.',
        errorMessagePrefix: 'Could not remove client',
      },
    );
    refresh();
  };

  return html`
    <div class="flex flex-col gap-2">
      <div class="flex flex-wrap gap-2">
        ${selectedClients.map(
          c =>
            html`<span class="badge badge-outline inline-flex items-center gap-1"
              >${c.name}<button type="button" class="btn btn-ghost btn-xs px-0" @click=${() => handleRemove(c._id)}>
                ×
              </button></span
            >`,
        )}
      </div>
      ${available.length
        ? html`<div class="max-w-md w-full min-w-0">
            ${DropdownSelectionInput<Client, false>({
              multi: false,
              placeholder: 'Add client to scope…',
              selectables: available,
              value: null,
              renderString: c => c.name ?? c._id,
              resolveID: c => c._id,
              emptyState: 'No clients',
              onPicked: v => {
                if (v) void handleAdd(v);
              },
            })}
          </div>`
        : nothing}
    </div>
  `;
});

export const nexusClientAssignmentSection: SectionRendererSection<AdornisUser> = {
  title: 'Nexus client scope',
  fields: [{ render: ({ entity: user }) => (!user ? nothing : ClientScopeEditor({ user })) }],
};
